import { useDispatch } from "react-redux";
import { CompetitionAdminActions, CompetitionAdminSelector, CompetitionAdminSlice } from "shared/reducers/CompetitionAdminSlice";
import { useCallback } from "react";
import { useHistory } from "react-router-dom";
import { CreateLinkTo } from "shared/constants/linkTo";
import { useSelector } from "react-redux";
import { STATUSES } from "_competition/const";
import { API, CompetitionAPI, Endpoints, TLang } from "@brg/api";
import { useTranslation } from "react-i18next";
import { useToasts } from "@brg/ui";
import { FolderT } from "shared/pages/designer/components/tabs/const";
import useQueryState from "shared/hooks/useQueryState";
import { BEST_FOLDER } from "shared/components/navigationSystem/const/const";
import { COMPETITION_TABS } from "../const";

export const useGetCompetitionsAdminPageActions = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { t } = useTranslation();
    const toasts = useToasts()
    const [{ tab }] = useQueryState();

    const { 
        isTimelineVisible,
        selectedCompetitions,
        folders,
        currentStatusForTab
    } = useSelector(CompetitionAdminSelector);

    const handleSetCurrentStatusForTab = useCallback((status: COMPETITION_TABS) => {
        dispatch(CompetitionAdminSlice.actions.setCurrentStatusForTab(status));
    }, [dispatch]);

    const handleFolderCreate = useCallback(async() => {
        // const createdType = tab === BEST_FOLDER.id ? 'rating' : 'regular';
        dispatch(CompetitionAdminSlice.actions.setIsCreated({ isCreated: true, createdType: 'folder' }));
    }, [dispatch]);

    const handleCompetitionCreate = () => {
        // if (currentTab === COMPETITION_ADMIN_TABS.ratings) {
        //   setModalOpen(true);
        // } else if (currentTab === COMPETITION_ADMIN_TABS.regular /*|| currentTab === COMPETITION_ADMIN_TABS.brief*/) {
        //   history.push(`${CreateLinkTo.competitionCreate()}?competitionTypePreset=withQuestionnaire`);
        // } else if (currentTab === COMPETITION_ADMIN_TABS.brief) {
        //   history.push(CreateLinkTo.competitionBriefCreate());
        // } else {
        //   history.push(`${CreateLinkTo.competitionAutomaticCreate()}?competitionTypePreset=automatic`);
        // }
        if (tab === BEST_FOLDER.id) {
            dispatch(CompetitionAdminSlice.actions.setIsCreated({ isCreated: true, createdType: 'rating' }));
            return;
        }
    
        history.push(`${CreateLinkTo.competitionCreate()}?competitionTypePreset=withQuestionnaire`);
    };
    
    const handleToggleTimeline = () => {
        dispatch(CompetitionAdminActions.toggleTimeline());
    };

    const onChangeStatus = useCallback(async(status: STATUSES | null) => {
        if (status === null || !selectedCompetitions?.length) {
            return;
        }

        await API.request()
        .endpoint(Endpoints.competitions)
        .url('/api/Competition/BulkUpdateStatus')
        .body({
            competitionIds: selectedCompetitions,
            status: Number(status)
        })
        .post()
        .send()
        .then((res: any) => {
            toasts.info(t('competitions_edit.admin_page.toasts.success'));
        }).catch(() => {
            toasts.error(t('competitions_edit.admin_page.toasts.error'));
        }).finally(() => {
            dispatch(CompetitionAdminSlice.actions.clearSelectedCompetitions());
            dispatch(CompetitionAdminSlice.actions.updateCompetitions({ selectedCompetitions, status: Number(status) }));
        })
    }, [selectedCompetitions, toasts, dispatch, t]);

    const onDeleteCompetitions = useCallback(
        async (folderId: string, competitionIds: string[]) => {
            const folder = folders?.find((folder: FolderT) => folder?.id === folderId);
            if (!folder) {
                return;
            }

            if (folder?.entityIds?.some((id: string) => competitionIds.includes(id))) {
                const removedEntityIds = folder?.entityIds?.filter((id: string) => competitionIds.includes(id));
                const filteredEntityIds = folder?.entityIds?.filter((id: string) => !competitionIds.includes(id));
                return await API.fetch(`${Endpoints.folders}/api/Folder/DeleteEntityIdsForFolder`, {
                    method: 'PUT',
                    headers: {
                        'Content-type': 'application/json; charset=UTF-8'
                    },
                    body: JSON.stringify({ 
                        folderId: folder?.id, 
                        entityIds: removedEntityIds 
                    })
                }).then((r) => {
                    return dispatch(
                        CompetitionAdminSlice.actions.updateFolderAfterMove(
                            { 
                                folderId: folder?.id ?? '', entityIds: filteredEntityIds ?? []   
                        }));
                })
            }
            
            return true;
    } , [folders])

    const checkAndRemoveCompetitionsFromFolder = useCallback(
        async (folder: FolderT, competitionIds: string[]) => {

            if (folder?.entityIds?.some((id: string) => competitionIds.includes(id))) {
                const removedEntityIds = folder?.entityIds?.filter((id: string) => competitionIds.includes(id));
                const filteredEntityIds = folder?.entityIds?.filter((id: string) => !competitionIds.includes(id));
                return await API.fetch(`${Endpoints.folders}/api/Folder/DeleteEntityIdsForFolder`, {
                    method: 'PUT',
                    headers: {
                        'Content-type': 'application/json; charset=UTF-8'
                    },
                    body: JSON.stringify({ 
                        folderId: folder?.id, 
                        entityIds: removedEntityIds 
                    })
                }).then((r) => {
                    return dispatch(
                        CompetitionAdminSlice.actions.updateFolderAfterMove(
                            { 
                                folderId: folder?.id ?? '', entityIds: filteredEntityIds ?? []   
                        }));
                })
            }
            
            return true;
    } , [])

    const onUpdateFolder = useCallback(
        async (data: FolderT, type: any, noToast?: boolean): Promise<any> => {
            return await API.fetch(`${Endpoints.folders}/api/Folder/UpdateFolder`, {
                method: 'PUT',
                headers: {
                    'Content-type': 'application/json; charset=UTF-8'
                },
                body: JSON.stringify(data),
            })
            .then((r) => {
                // if (r?.status === 200) {
                //     !noToast && info(t('folders.modal.success_edit'))
                // }
                dispatch(CompetitionAdminSlice.actions.updateFolderAfterSave({ folderId: data.id ?? '', name: JSON.parse(data.name ?? '{}') as TLang[] }));
                return r
            })
    }, [])

    const onDeleteFolder = useCallback(
        async (id: string, noToast?: boolean ) => {
            return await API.fetch(`${Endpoints.folders}/api/Folder/DeleteFolder`, {
                method: 'DELETE',
                headers: {
                    'Content-type': 'application/json; charset=UTF-8'
                },
                body: JSON.stringify(id)
            }).then((r) => {
                // if (!noToast && r?.status === 200) {
                //     info(t('folders.modal.success_delete_folder'))
                // }
                dispatch(CompetitionAdminSlice.actions.setFolders(folders?.filter((folder: FolderT) => folder.id !== id) ?? []));

                return r;
            })
    } ,[])

    const onAddCompetitionsToFolder = useCallback(
        async (folder: FolderT, competitionIds: string[]) => {

            
            const filteredCompetitionIds = competitionIds?.filter((id: string) => !folder?.entityIds?.includes(id));
            return await API.fetch(`${Endpoints.folders}/api/Folder/AddEntityIdsForFolder`, {
                method: 'PUT',
                headers: {
                    'Content-type': 'application/json; charset=UTF-8'
                },
                body: JSON.stringify({ 
                    folderId: folder?.id, 
                    entityIds: filteredCompetitionIds 
                })
            })
            .then((r) => {
                return dispatch(
                    CompetitionAdminSlice.actions.updateFolderAfterMove(
                        { 
                            folderId: folder?.id ?? '', 
                            entityIds: [...(folder?.entityIds ?? []), ...filteredCompetitionIds] })
                );
            })
    } , [])

    const onMoveCompetitions = useCallback(
        async (folderId: string, competitionIds: string[]) => {

            const folder = folders?.find((folder: FolderT) => folder?.id === folderId);

            if (!folder) {
                return;
            }

            // const prevFolder = folders?.find((folder: FolderT) => folder?.entityIds?.some((id: string) => competitionIds.includes(id)));

            // if (folderId === prevFolder?.id) {
            //     await checkAndRemoveCompetitionsFromFolder(folder, competitionIds);
            // } else {
            //     await onAddCompetitionsToFolder(folder, competitionIds);
            // }
            // if (prevFolder) {
            //     await checkAndRemoveCompetitionsFromFolder(prevFolder, competitionIds);
            // }

            await onAddCompetitionsToFolder(folder, competitionIds);

        } , [folders])

    return {
        onMoveCompetitions,
        onTabChange: handleSetCurrentStatusForTab,
        onCreateFolder: handleFolderCreate,
        onCreateCompetition: handleCompetitionCreate,
        onToggleTimeline: handleToggleTimeline,
        onUpdateFolder,
        onDeleteFolder,
        onDeleteCompetitions,
        onChangeStatus
    }
}import { useDispatch } from "react-redux";
import { CompetitionAdminActions, CompetitionAdminSelector, CompetitionAdminSlice } from "shared/reducers/CompetitionAdminSlice";
import { useCallback } from "react";
import { useHistory } from "react-router-dom";
import { CreateLinkTo } from "shared/constants/linkTo";
import { useSelector } from "react-redux";
import { STATUSES } from "_competition/const";
import { API, CompetitionAPI, Endpoints, TLang } from "@brg/api";
import { useTranslation } from "react-i18next";
import { useToasts } from "@brg/ui";
import { FolderT } from "shared/pages/designer/components/tabs/const";
import useQueryState from "shared/hooks/useQueryState";
import { BEST_FOLDER } from "shared/components/navigationSystem/const/const";
import { COMPETITION_TABS } from "../const";

export const useGetCompetitionsAdminPageActions = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { t } = useTranslation();
    const toasts = useToasts()
    const [{ tab }] = useQueryState();

    const { 
        isTimelineVisible,
        selectedCompetitions,
        folders,
        currentStatusForTab
    } = useSelector(CompetitionAdminSelector);

    const handleSetCurrentStatusForTab = useCallback((status: COMPETITION_TABS) => {
        dispatch(CompetitionAdminSlice.actions.setCurrentStatusForTab(status));
    }, [dispatch]);

    const handleFolderCreate = useCallback(async() => {
        // const createdType = tab === BEST_FOLDER.id ? 'rating' : 'regular';
        dispatch(CompetitionAdminSlice.actions.setIsCreated({ isCreated: true, createdType: 'folder' }));
    }, [dispatch]);

    const handleCompetitionCreate = () => {
        // if (currentTab === COMPETITION_ADMIN_TABS.ratings) {
        //   setModalOpen(true);
        // } else if (currentTab === COMPETITION_ADMIN_TABS.regular /*|| currentTab === COMPETITION_ADMIN_TABS.brief*/) {
        //   history.push(`${CreateLinkTo.competitionCreate()}?competitionTypePreset=withQuestionnaire`);
        // } else if (currentTab === COMPETITION_ADMIN_TABS.brief) {
        //   history.push(CreateLinkTo.competitionBriefCreate());
        // } else {
        //   history.push(`${CreateLinkTo.competitionAutomaticCreate()}?competitionTypePreset=automatic`);
        // }
        if (tab === BEST_FOLDER.id) {
            dispatch(CompetitionAdminSlice.actions.setIsCreated({ isCreated: true, createdType: 'rating' }));
            return;
        }
    
        history.push(`${CreateLinkTo.competitionCreate()}?competitionTypePreset=withQuestionnaire`);
    };
    
    const handleToggleTimeline = () => {
        dispatch(CompetitionAdminActions.toggleTimeline());
    };

    const onChangeStatus = useCallback(async(status: STATUSES | null) => {
        if (status === null || !selectedCompetitions?.length) {
            return;
        }

        await API.request()
        .endpoint(Endpoints.competitions)
        .url('/api/Competition/BulkUpdateStatus')
        .body({
            competitionIds: selectedCompetitions,
            status: Number(status)
        })
        .post()
        .send()
        .then((res: any) => {
            toasts.info(t('competitions_edit.admin_page.toasts.success'));
        }).catch(() => {
            toasts.error(t('competitions_edit.admin_page.toasts.error'));
        }).finally(() => {
            dispatch(CompetitionAdminSlice.actions.clearSelectedCompetitions());
            dispatch(CompetitionAdminSlice.actions.updateCompetitions({ selectedCompetitions, status: Number(status) }));
        })
    }, [selectedCompetitions, toasts, dispatch, t]);

    const onDeleteCompetitions = useCallback(
        async (folderId: string, competitionIds: string[]) => {
            const folder = folders?.find((folder: FolderT) => folder?.id === folderId);
            if (!folder) {
                return;
            }

            if (folder?.entityIds?.some((id: string) => competitionIds.includes(id))) {
                const removedEntityIds = folder?.entityIds?.filter((id: string) => competitionIds.includes(id));
                const filteredEntityIds = folder?.entityIds?.filter((id: string) => !competitionIds.includes(id));
                return await API.fetch(`${Endpoints.folders}/api/Folder/DeleteEntityIdsForFolder`, {
                    method: 'PUT',
                    headers: {
                        'Content-type': 'application/json; charset=UTF-8'
                    },
                    body: JSON.stringify({ 
                        folderId: folder?.id, 
                        entityIds: removedEntityIds 
                    })
                }).then((r) => {
                    return dispatch(
                        CompetitionAdminSlice.actions.updateFolderAfterMove(
                            { 
                                folderId: folder?.id ?? '', entityIds: filteredEntityIds ?? []   
                        }));
                })
            }
            
            return true;
    } , [folders])

    const checkAndRemoveCompetitionsFromFolder = useCallback(
        async (folder: FolderT, competitionIds: string[]) => {

            if (folder?.entityIds?.some((id: string) => competitionIds.includes(id))) {
                const removedEntityIds = folder?.entityIds?.filter((id: string) => competitionIds.includes(id));
                const filteredEntityIds = folder?.entityIds?.filter((id: string) => !competitionIds.includes(id));
                return await API.fetch(`${Endpoints.folders}/api/Folder/DeleteEntityIdsForFolder`, {
                    method: 'PUT',
                    headers: {
                        'Content-type': 'application/json; charset=UTF-8'
                    },
                    body: JSON.stringify({ 
                        folderId: folder?.id, 
                        entityIds: removedEntityIds 
                    })
                }).then((r) => {
                    return dispatch(
                        CompetitionAdminSlice.actions.updateFolderAfterMove(
                            { 
                                folderId: folder?.id ?? '', entityIds: filteredEntityIds ?? []   
                        }));
                })
            }
            
            return true;
    } , [])

    const onUpdateFolder = useCallback(
        async (data: FolderT, type: any, noToast?: boolean): Promise<any> => {
            return await API.fetch(`${Endpoints.folders}/api/Folder/UpdateFolder`, {
                method: 'PUT',
                headers: {
                    'Content-type': 'application/json; charset=UTF-8'
                },
                body: JSON.stringify(data),
            })
            .then((r) => {
                // if (r?.status === 200) {
                //     !noToast && info(t('folders.modal.success_edit'))
                // }
                dispatch(CompetitionAdminSlice.actions.updateFolderAfterSave({ folderId: data.id ?? '', name: JSON.parse(data.name ?? '{}') as TLang[] }));
                return r
            })
    }, [])

    const onDeleteFolder = useCallback(
        async (id: string, noToast?: boolean ) => {
            return await API.fetch(`${Endpoints.folders}/api/Folder/DeleteFolder`, {
                method: 'DELETE',
                headers: {
                    'Content-type': 'application/json; charset=UTF-8'
                },
                body: JSON.stringify(id)
            }).then((r) => {
                // if (!noToast && r?.status === 200) {
                //     info(t('folders.modal.success_delete_folder'))
                // }
                dispatch(CompetitionAdminSlice.actions.setFolders(folders?.filter((folder: FolderT) => folder.id !== id) ?? []));

                return r;
            })
    } ,[])

    const onAddCompetitionsToFolder = useCallback(
        async (folder: FolderT, competitionIds: string[]) => {

            
            const filteredCompetitionIds = competitionIds?.filter((id: string) => !folder?.entityIds?.includes(id));
            return await API.fetch(`${Endpoints.folders}/api/Folder/AddEntityIdsForFolder`, {
                method: 'PUT',
                headers: {
                    'Content-type': 'application/json; charset=UTF-8'
                },
                body: JSON.stringify({ 
                    folderId: folder?.id, 
                    entityIds: filteredCompetitionIds 
                })
            })
            .then((r) => {
                return dispatch(
                    CompetitionAdminSlice.actions.updateFolderAfterMove(
                        { 
                            folderId: folder?.id ?? '', 
                            entityIds: [...(folder?.entityIds ?? []), ...filteredCompetitionIds] })
                );
            })
    } , [])

    const onMoveCompetitions = useCallback(
        async (folderId: string, competitionIds: string[]) => {

            const folder = folders?.find((folder: FolderT) => folder?.id === folderId);

            if (!folder) {
                return;
            }

            // const prevFolder = folders?.find((folder: FolderT) => folder?.entityIds?.some((id: string) => competitionIds.includes(id)));

            // if (folderId === prevFolder?.id) {
            //     await checkAndRemoveCompetitionsFromFolder(folder, competitionIds);
            // } else {
            //     await onAddCompetitionsToFolder(folder, competitionIds);
            // }
            // if (prevFolder) {
            //     await checkAndRemoveCompetitionsFromFolder(prevFolder, competitionIds);
            // }

            await onAddCompetitionsToFolder(folder, competitionIds);

        } , [folders])

    return {
        onMoveCompetitions,
        onTabChange: handleSetCurrentStatusForTab,
        onCreateFolder: handleFolderCreate,
        onCreateCompetition: handleCompetitionCreate,
        onToggleTimeline: handleToggleTimeline,
        onUpdateFolder,
        onDeleteFolder,
        onDeleteCompetitions,
        onChangeStatus
    }
}